import React from 'react';
import { ExecutiveSummaryData } from './executive-summary';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Duration } from './Duration';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Status, StatusValue } from './StatusSelector';
import { ProfileInfo } from '../../../ML/components/ProfileInfo';
import { KeyFindings } from './KeyFindings';
import moment from 'moment';
import { CarePlanSelector } from './CarePlanSelector';

const messages = defineMessages({
  lastEditedBy: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.lastEditedBy',
    defaultMessage: 'Last edited by {name} on {time}',
  },
  signedOffBy: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signedOffBy',
    defaultMessage: 'Signed off on {time} by {name}',
  },
  intro1: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro',
    defaultMessage: 'The Personalized {membershipTier} Journey',
  },
  intro2: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro2',
    defaultMessage: 'Evaluation & Recommendations',
  },
  intro3: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro3',
    defaultMessage: 'prepared for {patientName}',
  },
});

const useStyles = makeStyles(theme => ({
  sideBar: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    height: '100%',
    flex: 1,
    maxHeight: '100%',
    minHeight: 0,
    overflowY: 'auto',
  },
  intro1: {
    ...theme.typography.h3,
  },
  intro2: {
    ...theme.typography.h2,
    color: theme.palette.text.primary,
  },
  intro3: {
    ...theme.typography.h4,
  },
}));

export const Metadata = ({
  executiveSummary,
  isSaving,
  isLoading,
  onStatusChange,
  onDurationChange,

  onFindingsChange,

  carePlans,
  onChangeCarePlan,
}: {
  executiveSummary: ExecutiveSummaryData;
  isSaving: boolean;
  isEditing: boolean;
  isLoading: boolean;
  onStatusChange: (status: StatusValue) => void;
  onDurationChange: (duration: { start?: Date; end?: Date }) => void;
  onFindingsChange: (findings: string[]) => void;
  carePlans: fhir.CarePlan[];
  onChangeCarePlan: (carePlanId: string) => void;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  return (
    <Box direction="column" className={classes.sideBar} gap={2}>
      <CarePlanSelector
        carePlans={carePlans}
        activeCarePlanId={executiveSummary.carePlan.id!}
        onChangeCarePlan={onChangeCarePlan}
      />

      <Duration
        executiveSummary={executiveSummary}
        onDurationChange={duration => onDurationChange(duration)}
        disabled={isLoading || isSaving}
        isEditing={executiveSummary.status !== 'completed'}
      />
      <KeyFindings
        onFindingsChange={findings => onFindingsChange(findings)}
        editingState={executiveSummary.getKeyFindings()}
        isDisabled={isLoading || isSaving}
        isEditing={executiveSummary.status !== 'completed'}
      />

      <Status
        executiveSummary={executiveSummary}
        onStatusChange={status => onStatusChange(status)}
      />

      {executiveSummary.getLastEditedBy() && (
        <Box marginTop="auto">
          <Text size="caption" weight="light">
            {formatMessage(
              executiveSummary.status === 'completed'
                ? messages.signedOffBy
                : messages.lastEditedBy,
              {
                name: (
                  <ProfileInfo userId={executiveSummary.getLastEditedBy()} />
                ),
                time: moment(
                  executiveSummary.carePlan.meta?.lastUpdated,
                ).format('MMM DD @ hh:mm a'),
              },
            )}
          </Text>
        </Box>
      )}
    </Box>
  );
};
